import React, { useMemo } from 'react';
import { Group, Line, Rect, Circle } from 'react-konva';
import type { MapDrawing, MapRegion, TerrainType, RenderedImageRect, InteractionState, Point, JsonValue } from '../../types/types';
import { useGridCalculator } from '../../hooks/useGridCalculator';
import { useGridService } from '../../hooks/useGridService';
import { DrawingType } from '../../types/drawing-types';

const applyAlphaToColor = (colorStr: string, alpha: number): string => {
  if (!colorStr) return `rgba(0,0,0,${alpha})`;
  if (colorStr.startsWith('#')) {
    const r = parseInt((colorStr ?? []).slice(1, 3), 16);
    const g = parseInt((colorStr ?? []).slice(3, 5), 16);
    const b = parseInt((colorStr ?? []).slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const match = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) {
    return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
  }
  return colorStr;
};

// Type guard to check if a value is an array of Points
const isPointsArray = (value: unknown): value is Point[] => {
  return Array.isArray(value) && value.every(p =>
    typeof p === 'object' && p !== null && 'x' in p && 'y' in p && typeof p.x === 'number' && typeof p.y === 'number'
  );
};

// Type guard to check for shape geometry properties
const isShapeDrawing = (geometry: Partial<MapDrawing['geometry']>): geometry is { posX: number; posY: number; width: number; height: number } => {
  return typeof geometry === 'object' && geometry?.posX != null && geometry.posY != null && geometry.width != null && geometry.height != null;
};

// Type guard to check for circle geometry properties
const isCircleDrawing = (geometry: Partial<MapDrawing['geometry']>): geometry is { cx: number; cy: number; radius: number } => {
  return typeof geometry === 'object' && geometry?.cx != null && geometry.cy != null && geometry.radius != null;
};
const isPathDrawing = (geometry: Partial<MapDrawing['geometry']>): geometry is { points: Point[] } => {
  return typeof geometry === 'object' && geometry !== null && 'points' in geometry && isPointsArray(geometry.points);
};

interface KonvaDrawingsLayerProps {
  imageRect: RenderedImageRect | null;
  interaction: InteractionState | null;
  mapDrawings: MapDrawing[];
  mapRegions: MapRegion[];
  activeMapId: number | null | undefined;
  terrainTypes: TerrainType[];
  showMapRegions: boolean | undefined;
}

const KonvaDrawingsLayer: React.FC<KonvaDrawingsLayerProps> = ({
  imageRect, interaction, mapDrawings, mapRegions, activeMapId, terrainTypes, showMapRegions,
}) => {
  // Computation hooks (not Zustand store hooks) - allowed in presenter per ADR-034
  const { gridSettings, getCellRenderData } = useGridCalculator({ imageRect });
  const gridService = useGridService(imageRect);

  const terrainColorMap = useMemo(() => new Map((terrainTypes ?? []).map((t: TerrainType) => [t.id, t.color])), [terrainTypes]);

  const visibleDrawings = useMemo(() => {
    return (mapDrawings ?? []).filter((d: MapDrawing) => d.mapId === activeMapId && d.visible !== false);
  }, [mapDrawings, activeMapId]);

  const visibleRegions = useMemo(() => {
    // Exclude the region currently being drawn by the interaction layer
    const activeRegionId = interaction?.type === 'region' ? interaction.id : null;
    return (mapRegions ?? []).filter((r: MapRegion) =>
      String(r.id) !== activeRegionId && r.mapId === activeMapId && r.visible !== false
    );
  }, [mapRegions, activeMapId, interaction]);

  if (!imageRect || imageRect.width === 0) {
    return null;
  }

  const journeyColor = '#3399ff'; // This is now defined in useMapInteraction, but keeping it for existing drawings
  const drawingColor = '#ff3333';
  const shapeColor = '#ff9900';
  const defaultRegionColor = 'rgba(156, 39, 176, 0.4)';

  return (
    <Group listening={false}>
      {/* Render Drawings */}
      {(visibleDrawings ?? []).map((drawing: MapDrawing) => {
        switch (drawing.type) {
          case DrawingType.line:
          case DrawingType.journey: {
            if (!drawing.geometry || !isPathDrawing(drawing.geometry) || !drawing.geometry.points) return null;
            const points = drawing.geometry.points as unknown as Point[];
            const linePoints = (points ?? []).flatMap(p => [
              imageRect.x + imageRect.width * (p.x / 100),
              imageRect.y + imageRect.height * (p.y / 100),
            ]);
            const lineColor = drawing.type === 'journey' ? journeyColor : drawingColor;
            return <Line key={drawing.id} points={linePoints} stroke={drawing.color || lineColor} strokeWidth={2} tension={0.5} />;
          }
          case DrawingType.grid_path: {
            if (!drawing.geometry || !isPathDrawing(drawing.geometry) || !drawing.geometry.points || (drawing.geometry.points as unknown as Point[]).length !== 2 || gridSettings.type !== 'hex') return null;
            const points = drawing.geometry.points as unknown as Point[];
            const startPointPath = { x: imageRect.x + imageRect.width * (points[0]!.x / 100), y: imageRect.y + imageRect.height * (points[0]!.y / 100) };
            const endPointPath = { x: imageRect.x + imageRect.width * (points[1]!.x / 100), y: imageRect.y + imageRect.height * (points[1]!.y / 100) };
            const startHexPath = gridService.pointToHex(startPointPath);
            const endHexPath = gridService.pointToHex(endPointPath);
            const hexesOnPath = gridService.getHexesBetween(startHexPath, endHexPath);
            const pathPoints = (hexesOnPath ?? []).flatMap((hex) => {
              const center = hex.center;
              return [center.x, center.y];
            });
            return <Line key={drawing.id} points={pathPoints} stroke={drawing.color || '#39e639'} strokeWidth={3} tension={0} />;
          }
          case DrawingType.cone: {
            if (!drawing.geometry || !isPathDrawing(drawing.geometry) || !drawing.geometry.points || (drawing.geometry.points as unknown as Point[]).length !== 2 || gridSettings.type !== 'hex') return null;
            const points = drawing.geometry.points as unknown as Point[];
            const startPointCone = { x: imageRect.x + imageRect.width * (points[0]!.x / 100), y: imageRect.y + imageRect.height * (points[0]!.y / 100) };
            const endPointCone = { x: imageRect.x + imageRect.width * (points[1]!.x / 100), y: imageRect.y + imageRect.height * (points[1]!.y / 100) };
            const startHexCone = gridService.pointToHex(startPointCone);
            const endHexCone = gridService.pointToHex(endPointCone);
            const angle = Math.atan2(endHexCone.y - startHexCone.y, endHexCone.x - startHexCone.x) * 180 / Math.PI;
            const direction = Math.round(angle / 60 + 6) % 6;
            const radius = gridService.distance(startHexCone, endHexCone) as number;
            const hexesInCone = gridService.getCone({ start: startHexCone, direction, radius });
            return (hexesInCone ?? []).map((hex) => {
              const conePoints = (hex.corners ?? []).map((p: { x: number, y: number }) => [p.x, p.y]).flat();
              return <Line key={`${drawing.id}-${hex.q}-${hex.r}`} points={conePoints} fill={drawing.color ? applyAlphaToColor(drawing.color, 0.5) : 'rgba(255, 77, 79, 0.5)'} closed />;
            });
          }
          case DrawingType.rect: {
            if (!drawing.geometry || !isShapeDrawing(drawing.geometry)) return null;
            return (
              <Rect
                key={drawing.id}
                x={imageRect.x + imageRect.width * (Number(drawing.geometry.posX ?? 0) / 100)}
                y={imageRect.y + imageRect.height * (Number(drawing.geometry.posY ?? 0) / 100)}
                width={imageRect.width * (Number(drawing.geometry.width ?? 0) / 100)}
                height={imageRect.height * (Number(drawing.geometry.height ?? 0) / 100)}
                stroke={drawing.color || shapeColor}
                strokeWidth={2}
              />
            );
          }
          case DrawingType.circle: {
            if (!drawing.geometry || !isCircleDrawing(drawing.geometry)) return null;
            return (
              <Circle
                key={drawing.id}
                x={imageRect.x + imageRect.width * (Number(drawing.geometry.cx ?? 0) / 100)}
                y={imageRect.y + imageRect.height * (Number(drawing.geometry.cy ?? 0) / 100)}
                radius={imageRect.width * (Number(drawing.geometry.radius ?? 0) / 100)} // Assume radius is relative to width
                stroke={drawing.color || shapeColor}
                strokeWidth={2}
              />
            );
          }
          default:
            return null;
        }
      })}

      {/* Render Regions */}
      {gridSettings?.enabled && showMapRegions && visibleRegions.flatMap((region: MapRegion) => {
        // Prioritize the region's own color. Fallback to terrain, then to default.
        let fillColor = region.color;
        if (!fillColor && region.terrainTypeId && terrainColorMap.has(region.terrainTypeId)) {
          fillColor = terrainColorMap.get(region.terrainTypeId) as string;
        }
        const finalColor = applyAlphaToColor(fillColor ?? defaultRegionColor, Number(region.opacity ?? 0.8));

        if (!region.cells) return null;
        return (region.cells as JsonValue as { row: number, col: number }[]).map((cell: { row: number, col: number }) => {
          const cellData = getCellRenderData(cell.row, cell.col);
          if (!cellData) return null;

          const key = `${region.id}-${cell.row}-${cell.col}`;
          if (region.type === 'square' && gridSettings.type === 'square') {
            return <Rect key={key} x={cellData.x} y={cellData.y} width={cellData.width} height={cellData.height} fill={finalColor} />;
          } if (region.type === 'hex' && gridSettings.type === 'hex' && cellData.points) {
            const points = cellData.points.split(' ').flatMap((p: string) => p.split(',').map(Number));
            return <Line key={key} points={points} fill={finalColor} closed />;
          }
          return null;
        });
      })}
    </Group>
  );
};

export default KonvaDrawingsLayer;
